import { useEffect, useMemo, useState } from 'react'
import Card from '../../components/Card'
import Toast from '../../components/Toast'
import { useBusinessSettingsContext } from '../../hooks/useBusinessSettingsContext'
import './AdminBusinessSettings.css'

const RUBRO_LABELS = {
  muebles: 'Muebles',
  indumentaria: 'Indumentaria',
  electrodomesticos: 'Electrodomésticos',
  tecnologia: 'Tecnología'
}

const INDUMENTARIA_SUBRUBRO_LABELS = {
  femenina: 'Femenina',
  masculina: 'Masculina',
  mixta: 'Mixta'
}

function AdminBusinessSettings() {
  const { settings, loading, error, update, refetch } = useBusinessSettingsContext()
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    if (settings) {
      setDraft(settings)
    }
  }, [settings])

  const activeRubro = draft?.active?.rubro || 'muebles'
  const activeSubrubro = draft?.active?.subrubro || null

  const rubroConfig = useMemo(() => draft?.rubros?.[activeRubro] || null, [draft, activeRubro])

  const availableDesigns = useMemo(() => {
    if (!rubroConfig) return []
    if (activeRubro !== 'indumentaria') {
      return rubroConfig.designs || []
    }
    const sub = activeSubrubro || 'femenina'
    return rubroConfig.subrubros?.[sub]?.designs || []
  }, [rubroConfig, activeRubro, activeSubrubro])

  const setActive = (next) => {
    setDraft(prev => ({
      ...prev,
      active: {
        ...prev.active,
        ...next
      }
    }))
  }

  const handleSelectRubro = (rubro) => {
    const conf = draft?.rubros?.[rubro]
    if (!conf?.enabled) return

    if (rubro === 'indumentaria') {
      const sub = Object.keys(conf.subrubros || {}).find(key => conf.subrubros[key]?.enabled) || 'femenina'
      const designId = conf.subrubros?.[sub]?.defaultDesignId || conf.subrubros?.[sub]?.designs?.[0] || 'indumentaria/femenina/v1'
      setActive({ rubro, subrubro: sub, designId })
      return
    }

    const designId = conf.defaultDesignId || conf.designs?.[0] || 'muebles/v1'
    setActive({ rubro, subrubro: null, designId })
  }

  const handleSelectSubrubro = (subrubro) => {
    const conf = draft?.rubros?.indumentaria?.subrubros?.[subrubro]
    if (!conf?.enabled) return
    const designId = conf.defaultDesignId || conf.designs?.[0] || draft?.active?.designId
    setActive({ rubro: 'indumentaria', subrubro, designId })
  }

  const handleDesignChange = (designId) => {
    setActive({ designId })
  }

  const handleSave = async () => {
    if (!draft || saving) return
    setSaving(true)
    try {
      await update(draft)
      setNotification({ type: 'success', message: 'Configuración comercial guardada' })
      setTimeout(() => setNotification(null), 2500)
    } catch (e) {
      setNotification({ type: 'error', message: 'Error guardando la configuración comercial' })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !draft) {
    return (
      <div className="admin-business-settings-page">
        <div className="admin-business-settings-header">
          <h2>Configuración Comercial</h2>
          <p>Define el rubro activo y su diseño público.</p>
        </div>
        <div className="admin-business-settings-loading">Cargando configuración...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-business-settings-page">
        <div className="admin-business-settings-header">
          <h2>Configuración Comercial</h2>
          <p>Define el rubro activo y su diseño público.</p>
        </div>
        <div className="admin-business-settings-error">
          <p>{error}</p>
          <button className="admin-business-settings-retry" onClick={refetch}>
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-business-settings-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'success'}
        onClose={() => setNotification(null)}
      />

      <div className="admin-business-settings-header">
        <h2>Configuración Comercial</h2>
        <p>Selecciona el rubro activo (solo uno a la vez) y el diseño de la tienda pública.</p>
      </div>

      <div className="admin-business-settings-grid">
        {Object.keys(RUBRO_LABELS).map((rubroKey) => {
          const conf = draft.rubros?.[rubroKey]
          const enabled = !!conf?.enabled
          const isSelected = activeRubro === rubroKey
          return (
            <Card
              key={rubroKey}
              variant="default"
              className={[
                'admin-business-settings-card',
                isSelected ? 'selected' : '',
                enabled ? '' : 'disabled'
              ].join(' ').trim()}
              onClick={() => handleSelectRubro(rubroKey)}
            >
              <div className="admin-business-settings-card-inner">
                <div className="admin-business-settings-card-title">
                  <h3>{RUBRO_LABELS[rubroKey]}</h3>
                  <span className={`admin-business-settings-badge ${enabled ? 'ok' : 'off'}`}>
                    {enabled ? (isSelected ? 'Activo' : 'Habilitado') : 'Inhabilitado'}
                  </span>
                </div>
                <p className="admin-business-settings-card-desc">
                  {rubroKey === 'muebles' && 'Usa la tienda pública actual.'}
                  {rubroKey === 'indumentaria' && 'Permite seleccionar subrubro y diseño.'}
                  {rubroKey === 'electrodomesticos' && 'Pendiente de diseño y datos.'}
                  {rubroKey === 'tecnologia' && 'Pendiente de diseño y datos.'}
                </p>
              </div>
            </Card>
          )
        })}
      </div>

      {activeRubro === 'indumentaria' ? (
        <div className="admin-business-settings-section">
          <div className="admin-business-settings-section-header">
            <h3>Subrubro</h3>
            <p>Define el tipo de indumentaria a publicar.</p>
          </div>

          <div className="admin-business-settings-subgrid">
            {Object.keys(INDUMENTARIA_SUBRUBRO_LABELS).map((subKey) => {
              const conf = draft.rubros?.indumentaria?.subrubros?.[subKey]
              const enabled = !!conf?.enabled
              const isSelected = (activeSubrubro || 'femenina') === subKey
              return (
                <button
                  key={subKey}
                  type="button"
                  className={[
                    'admin-business-settings-pill',
                    isSelected ? 'selected' : '',
                    enabled ? '' : 'disabled'
                  ].join(' ').trim()}
                  onClick={() => handleSelectSubrubro(subKey)}
                  disabled={!enabled}
                >
                  {INDUMENTARIA_SUBRUBRO_LABELS[subKey]}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      <div className="admin-business-settings-section">
        <div className="admin-business-settings-section-header">
          <h3>Diseño</h3>
          <p>Preparado para soportar múltiples diseños por rubro.</p>
        </div>

        <div className="admin-business-settings-row">
          <label className="admin-business-settings-label" htmlFor="designId">
            Diseño activo
          </label>
          <select
            id="designId"
            className="admin-business-settings-select"
            value={draft.active?.designId || ''}
            onChange={(e) => handleDesignChange(e.target.value)}
          >
            {availableDesigns.length === 0 ? (
              <option value="">Sin diseños disponibles</option>
            ) : (
              availableDesigns.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="admin-business-settings-summary">
        <h3>Resumen</h3>
        <div className="admin-business-settings-summary-grid">
          <div className="admin-business-settings-summary-item">
            <span className="k">Rubro activo</span>
            <span className="v">{RUBRO_LABELS[activeRubro]}</span>
          </div>
          <div className="admin-business-settings-summary-item">
            <span className="k">Subrubro</span>
            <span className="v">{activeRubro === 'indumentaria' ? INDUMENTARIA_SUBRUBRO_LABELS[activeSubrubro || 'femenina'] : '—'}</span>
          </div>
          <div className="admin-business-settings-summary-item">
            <span className="k">Diseño</span>
            <span className="v">{draft.active?.designId || '—'}</span>
          </div>
        </div>
      </div>

      <div className="admin-business-settings-actions">
        <button
          type="button"
          className="admin-business-settings-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  )
}

export default AdminBusinessSettings
